import { AxiosError } from 'axios';
import { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, FloatingLabel, Form, ListGroup, Modal, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import axiosInstance from 'utils/axiosInterceptors';
import { consumeSse } from 'utils/sseClient';
import { toastConfig } from 'utils/toastConfig';
import Button from 'components/base/Button';
import useGetProductBrand from 'pages/apps/hooks/useGetProductBrand';
import useGetProductCategory from 'pages/apps/hooks/useGetProductCategory';
import { SelectOption } from 'pages/apps/crm/PurchaseOrder/CreatePurchaseOrderForm';
import CategorySelect from 'components/shared/common/CategorySelect';
import * as yup from 'yup';

type ProductClashMatch = {
  productId: number;
  name: string;
  barcodeLabel: string;
  productBrand: string;
  productCategoryName: string;
  similarityPercent: number;
  clashReason: string;
  isBlocking: boolean;
  isAiSuggested: boolean;
};

type ProductClashCheckResult = {
  hasClashes: boolean;
  hasBlockingClash: boolean;
  usedAi: boolean;
  matches: ProductClashMatch[];
};

type ProposalFitment = {
  make: string;
  model: string;
  yearRange?: string | null;
  engine?: string | null;
  usage?: string | null;
};

type ProposalOnlinePrice = {
  source?: string | null;
  url?: string | null;
  amount?: number | null;
  currency?: string | null;
};

type ProposalOnlineReference = {
  title?: string | null;
  url: string;
  source?: string | null;
};

type ProposalExistingMatch = {
  productId: number;
  name?: string | null;
  brand?: string | null;
  category?: string | null;
  reason?: string | null;
  confidence: number;
  price?: number | null;
};

type CatalogProductProposalDraft = {
  identifier?: string | null;
  brandId?: number | null;
  brandName?: string | null;
  categoryId?: number | null;
  categoryName?: string | null;
  name?: string | null;
  description?: string | null;
  shortVehicleInfo?: string | null;
  barcodeLabel?: string | null;
  oemNumbers?: string[];
  fitments?: ProposalFitment[];
  imageUrls?: string[];
};

type CatalogProductProposal = {
  draft?: CatalogProductProposalDraft;
  existingMatches?: ProposalExistingMatch[];
  onlinePrices?: ProposalOnlinePrice[];
  onlineReferences?: ProposalOnlineReference[];
  suggestedSellingPrice?: number | null;
  usedAi?: boolean;
};

type ProposalStreamResult = {
  type: 'result';
  proposal?: CatalogProductProposal;
};

type CreateProductFromUnencodedModalProps = {
  show: boolean;
  handleClose: () => void;
  query: string;
  sourceLabel?: string;
  onBind: (productId: number) => Promise<void>;
};

const categoryLeafName = (categoryName?: string | null) => {
  if (!categoryName?.trim()) return '';
  const parts = categoryName
    .split(/[>/|]/)
    .map(part => part.trim())
    .filter(Boolean);
  return parts[parts.length - 1] || categoryName.trim();
};

const composeProductName = (
  brandName?: string | null,
  identifier?: string | null,
  categoryName?: string | null
) =>
  [brandName, identifier, categoryLeafName(categoryName)]
    .map(part => part?.trim())
    .filter(Boolean)
    .join(' | ');

const truncate = (value: string, max: number) =>
  value.length <= max ? value : value.slice(0, max).trimEnd();

const composeBarcodeLabel = (
  brandName?: string | null,
  identifier?: string | null,
  maxLength = 40
) => {
  const brand = truncate((brandName || '').trim(), 12);
  const id = truncate((identifier || '').trim(), 18);
  return truncate([brand, id].filter(Boolean).join(' | '), maxLength);
};

const resolveSelectOption = (
  options: SelectOption[],
  id?: number | null,
  name?: string | null
): SelectOption | null => {
  if (id != null) {
    const byId = options.find(o => o.value === id);
    if (byId) return byId;
    if (name?.trim()) return { value: id, label: name.trim() };
  }

  if (name?.trim()) {
    const normalized = name.trim().toLowerCase();
    return (
      options.find(o => o.label.toLowerCase() === normalized) ||
      options.find(o => o.label.toLowerCase().includes(normalized)) ||
      null
    );
  }

  return null;
};

const emptyFitment = (): ProposalFitment => ({
  make: '',
  model: '',
  yearRange: '',
  engine: '',
  usage: ''
});

const CreateProductFromUnencodedModal = ({
  show,
  handleClose,
  query,
  sourceLabel,
  onBind
}: CreateProductFromUnencodedModalProps) => {
  const [progressMessage, setProgressMessage] = useState('Looking up…');
  const [isResearching, setIsResearching] = useState(false);
  const [researchError, setResearchError] = useState<string | null>(null);
  const [proposal, setProposal] = useState<CatalogProductProposal | null>(null);

  const [productIdentifier, setProductIdentifier] = useState('');
  const [productName, setProductName] = useState('');
  const [nameManuallyEdited, setNameManuallyEdited] = useState(false);
  const [description, setDescription] = useState('');
  const [sellingPrice, setSellingPrice] = useState<number | string>('');
  const [barcodeLabel, setBarcodeLabel] = useState('');
  const [barcodeManuallyEdited, setBarcodeManuallyEdited] = useState(false);
  const [oemNumbers, setOemNumbers] = useState<string[]>([]);
  const [oemDraft, setOemDraft] = useState('');
  const [fitments, setFitments] = useState<ProposalFitment[]>([]);
  const [onlineImageUrls, setOnlineImageUrls] = useState<string[]>([]);
  const [selectedImageUrls, setSelectedImageUrls] = useState<string[]>([]);
  const [uploadedImages, setUploadedImages] = useState<File[]>([]);
  const [selectedBrand, setSelectedBrand] = useState<SelectOption | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<SelectOption | null>(
    null
  );
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [isCheckingClash, setIsCheckingClash] = useState(false);
  const [showClashConfirm, setShowClashConfirm] = useState(false);
  const [clashResult, setClashResult] = useState<ProductClashCheckResult | null>(
    null
  );
  const [flowPhase, setFlowPhase] = useState<
    'checking-clash' | 'clash-review' | 'researching' | 'form'
  >('checking-clash');

  const abortRef = useRef<AbortController | null>(null);
  const startResearchRef = useRef<() => void>(() => {});
  const { productBrands } = useGetProductBrand();
  const { productCategories } = useGetProductCategory(true);

  const brandOptions = useMemo<SelectOption[]>(() => {
    const options = productBrands.map(b => ({
      value: b.productBrandId,
      label: b.name
    }));
    if (selectedBrand && !options.some(o => o.value === selectedBrand.value)) {
      return [selectedBrand, ...options];
    }
    return options;
  }, [productBrands, selectedBrand]);

  const schema = useMemo(
    () =>
      yup.object().shape({
        productBrandId: yup.mixed().required('Brand is required'),
        productCategoryId: yup.mixed().required('Category is required'),
        productName: yup.string().required('Product name is required'),
        description: yup.string().required('Description is required'),
        sellingPrice: yup.string().required('Selling price is required'),
        barcodeLabel: yup.string().required('Barcode label is required')
      }),
    []
  );

  const resetForm = () => {
    setProductIdentifier('');
    setProductName('');
    setNameManuallyEdited(false);
    setDescription('');
    setSellingPrice('');
    setBarcodeLabel('');
    setBarcodeManuallyEdited(false);
    setOemNumbers([]);
    setOemDraft('');
    setFitments([]);
    setOnlineImageUrls([]);
    setSelectedImageUrls([]);
    setUploadedImages([]);
    setSelectedBrand(null);
    setSelectedCategory(null);
    setErrors({});
    setClashResult(null);
    setShowClashConfirm(false);
    setProposal(null);
    setResearchError(null);
    setIsResearching(false);
    setIsCheckingClash(false);
    setProgressMessage('');
    setFlowPhase('checking-clash');
  };

  const applyProposal = (next: CatalogProductProposal) => {
    const draft = next.draft ?? {};
    const identifier = draft.identifier?.trim() || query.trim();
    setProductIdentifier(identifier);
    setDescription(draft.description?.trim() || '');
    setProductName(
      draft.name?.trim() ||
        composeProductName(draft.brandName, identifier, draft.categoryName)
    );
    setNameManuallyEdited(false);
    setBarcodeLabel(
      draft.barcodeLabel?.trim() ||
        composeBarcodeLabel(draft.brandName, identifier)
    );
    setBarcodeManuallyEdited(false);
    setSellingPrice(
      next.suggestedSellingPrice != null ? next.suggestedSellingPrice : ''
    );
    setOemNumbers(draft.oemNumbers ?? []);
    setFitments(
      (draft.fitments ?? []).map(f => ({
        make: f.make || '',
        model: f.model || '',
        yearRange: f.yearRange || '',
        engine: f.engine || '',
        usage: f.usage || ''
      }))
    );
    const images = (draft.imageUrls ?? []).filter(
      (url): url is string => typeof url === 'string' && !!url.trim()
    );
    setOnlineImageUrls(images);
    setSelectedImageUrls(images);
    const brands = productBrands.map(b => ({
      value: b.productBrandId,
      label: b.name
    }));
    const categories = productCategories.map(c => ({
      value: c.productCategoryId,
      label: c.fullPath || c.name
    }));
    setSelectedBrand(
      resolveSelectOption(brands, draft.brandId, draft.brandName)
    );
    setSelectedCategory(
      resolveSelectOption(categories, draft.categoryId, draft.categoryName)
    );
    setProposal(next);
  };

  useEffect(() => {
    if (!show) {
      abortRef.current?.abort();
      abortRef.current = null;
      resetForm();
      return;
    }

    resetForm();
    const trimmed = query.trim();
    setProductIdentifier(trimmed);
    setProductName(trimmed);
    setBarcodeLabel(composeBarcodeLabel(null, trimmed));

    const controller = new AbortController();
    abortRef.current = controller;

    const beginResearch = () => {
      setFlowPhase('researching');
      setIsResearching(true);
      setClashResult(null);
      setShowClashConfirm(false);
      setProgressMessage(`Looking up ${trimmed}…`);

      const params = new URLSearchParams({ query: trimmed });
      if (sourceLabel?.trim()) params.set('source', sourceLabel.trim());

      void consumeSse<ProposalStreamResult>(
        `/api/v1/products/create-proposal/stream?${params.toString()}`,
        { method: 'GET', signal: controller.signal },
        {
          onProgress: (_stage, message) => {
            if (message) setProgressMessage(message);
          },
          onResult: data => {
            applyProposal(data.proposal ?? {});
            setIsResearching(false);
            setFlowPhase('form');
          },
          onError: (_code, message) => {
            setResearchError(message || 'Lookup failed');
            setIsResearching(false);
            setFlowPhase('form');
          }
        }
      ).catch(error => {
        if ((error as Error)?.name === 'AbortError') return;
        setResearchError('Lookup failed');
        setIsResearching(false);
        setFlowPhase('form');
      });
    };

    startResearchRef.current = beginResearch;

    const runInitialClashCheck = async () => {
      setFlowPhase('checking-clash');
      setIsCheckingClash(true);
      setProgressMessage(
        `Checking for existing products matching ${trimmed}…`
      );
      try {
        const { data } = await axiosInstance.get<ProductClashCheckResult>(
          `/api/v1/products/check-clash`,
          {
            params: {
              productName: trimmed,
              productIdentifier: trimmed || undefined,
              useAi: true,
              mode: 'Default'
            },
            signal: controller.signal
          }
        );
        if (controller.signal.aborted) return;
        if (data.hasClashes) {
          setClashResult(data);
          setFlowPhase('clash-review');
          return;
        }
        beginResearch();
      } catch (error) {
        if (
          (error as Error)?.name === 'AbortError' ||
          (error as AxiosError)?.code === 'ERR_CANCELED'
        ) {
          return;
        }
        setResearchError(
          'Clash check failed. You can still research and create the product.'
        );
        beginResearch();
      } finally {
        if (!controller.signal.aborted) setIsCheckingClash(false);
      }
    };

    void runInitialClashCheck();

    return () => {
      controller.abort();
    };
  }, [show, query, sourceLabel]);

  useEffect(() => {
    if (!show || !proposal?.draft) return;

    const brands = productBrands.map(b => ({
      value: b.productBrandId,
      label: b.name
    }));
    const categories = productCategories.map(c => ({
      value: c.productCategoryId,
      label: c.fullPath || c.name
    }));

    setSelectedBrand(prev =>
      prev ??
      resolveSelectOption(
        brands,
        proposal.draft?.brandId,
        proposal.draft?.brandName
      )
    );
    setSelectedCategory(prev =>
      prev ??
      resolveSelectOption(
        categories,
        proposal.draft?.categoryId,
        proposal.draft?.categoryName
      )
    );
  }, [show, proposal, productBrands, productCategories]);

  useEffect(() => {
    if (!show || nameManuallyEdited) return;
    const composed = composeProductName(
      selectedBrand?.label,
      productIdentifier,
      selectedCategory?.label
    );
    if (composed) setProductName(composed);
  }, [
    show,
    nameManuallyEdited,
    selectedBrand?.label,
    selectedCategory?.label,
    productIdentifier
  ]);

  useEffect(() => {
    if (!show || barcodeManuallyEdited) return;
    setBarcodeLabel(composeBarcodeLabel(selectedBrand?.label, productIdentifier));
  }, [show, barcodeManuallyEdited, selectedBrand?.label, productIdentifier]);

  const clearImageError = () => {
    setErrors(prev => {
      if (!prev.images) return prev;
      const next = { ...prev };
      delete next.images;
      return next;
    });
  };

  const hasProductImages = (urls = selectedImageUrls, files = uploadedImages) =>
    urls.length > 0 || files.length > 0;

  const toggleOnlineImage = (url: string) => {
    setSelectedImageUrls(prev =>
      prev.includes(url) ? prev.filter(item => item !== url) : [...prev, url]
    );
    clearImageError();
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    setUploadedImages(files);
    if (files.length > 0 || selectedImageUrls.length > 0) {
      clearImageError();
    }
  };

  const addOem = () => {
    const next = oemDraft.trim();
    if (!next) return;
    setOemNumbers(prev =>
      prev.some(o => o.toLowerCase() === next.toLowerCase())
        ? prev
        : [...prev, next]
    );
    setOemDraft('');
  };

  const bindExisting = async (productId: number) => {
    setIsSaving(true);
    try {
      await onBind(productId);
      handleClose();
      toast.success('Product assigned', {
        ...toastConfig,
        autoClose: 3000
      });
    } catch (error) {
      if (error instanceof AxiosError) {
        toast.error(
          error.response?.data?.errorMessage || 'Failed to assign product',
          { ...toastConfig, autoClose: 3000 }
        );
      }
    } finally {
      setIsSaving(false);
      setShowClashConfirm(false);
    }
  };

  const createProduct = async () => {
    if (!hasProductImages()) {
      setErrors(prev => ({
        ...prev,
        images: 'At least one product image is required'
      }));
      setShowClashConfirm(false);
      return;
    }

    setIsSaving(true);
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('ProductBrandId', selectedBrand!.value.toString());
      formDataToSend.append(
        'ProductCategoryId',
        selectedCategory!.value.toString()
      );
      formDataToSend.append('ProductName', productName);
      formDataToSend.append('Description', description);
      formDataToSend.append(
        'SellingPrice',
        sellingPrice === '' || sellingPrice == null
          ? '0'
          : sellingPrice.toString()
      );
      formDataToSend.append('BarcodeLabel', barcodeLabel);
      oemNumbers.forEach(oem => formDataToSend.append('OemNumbers', oem));
      if (selectedImageUrls.length > 0) {
        formDataToSend.append(
          'ImageUrlsJson',
          JSON.stringify(selectedImageUrls)
        );
        selectedImageUrls.forEach((url, index) => {
          formDataToSend.append(`ImageUrls[${index}]`, url);
          formDataToSend.append('ImageUrls', url);
        });
      }
      uploadedImages.forEach(file => formDataToSend.append('Images', file));
      const validFitments = fitments.filter(f => f.make.trim() && f.model.trim());
      if (validFitments.length > 0) {
        formDataToSend.append('FitmentsJson', JSON.stringify(validFitments));
      }

      const { data } = await axiosInstance.post<{ productId: number }>(
        `/api/v1/products`,
        formDataToSend
      );

      if (!data?.productId) {
        throw new Error('Product was created but no id was returned');
      }

      await onBind(data.productId);
      handleClose();
      toast.success('Product created and assigned', {
        ...toastConfig,
        autoClose: 3000
      });
    } catch (error) {
      if (error instanceof AxiosError) {
        toast.error(
          error.response?.data?.errorMessage || 'Failed to create product',
          { ...toastConfig, autoClose: 3000 }
        );
      }
    } finally {
      setIsSaving(false);
      setShowClashConfirm(false);
    }
  };

  const handleSubmit = async () => {
    const formData = {
      productBrandId: selectedBrand?.value,
      productCategoryId: selectedCategory?.value,
      productName,
      description,
      sellingPrice,
      barcodeLabel
    };

    try {
      await schema.validate(formData, { abortEarly: false });
      if (!hasProductImages()) {
        setErrors(prev => ({
          ...prev,
          images: 'At least one product image is required'
        }));
        return;
      }
      setErrors({});
      setIsCheckingClash(true);

      const { data } = await axiosInstance.get<ProductClashCheckResult>(
        `/api/v1/products/check-clash`,
        {
          params: {
            productName,
            productIdentifier: productIdentifier.trim() || undefined,
            barcodeLabel,
            description,
            productBrandId: selectedBrand?.value,
            productBrandName: selectedBrand?.label || undefined,
            productCategoryId: selectedCategory?.value,
            productCategoryName: selectedCategory?.label || undefined,
            useAi: true,
            mode: 'Default'
          }
        }
      );

      if (data.hasClashes) {
        setClashResult(data);
        setShowClashConfirm(true);
        return;
      }

      await createProduct();
    } catch (error) {
      if (error instanceof AxiosError) {
        toast.error(error.response?.data?.errorMessage, {
          ...toastConfig,
          autoClose: 3000
        });
      } else if (error instanceof yup.ValidationError) {
        const nextErrors: Record<string, string> = {};
        error.inner.forEach(err => {
          if (err.path) nextErrors[err.path] = err.message;
        });
        setErrors(nextErrors);
      }
    } finally {
      setIsCheckingClash(false);
    }
  };

  const isBusy = isSaving || isCheckingClash || isResearching;
  const existingMatches = proposal?.existingMatches ?? [];
  const onlinePrices = proposal?.onlinePrices ?? [];
  const onlineReferences = proposal?.onlineReferences ?? [];

  return (
    <>
      <Modal show={show} onHide={handleClose} size="xl" scrollable>
        <Modal.Header closeButton>
          <Modal.Title>Create product from {query.trim() || 'unencoded row'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {flowPhase === 'checking-clash' && (
            <Alert variant="info" className="d-flex align-items-center gap-2">
              <Spinner animation="border" size="sm" />
              <span>{progressMessage}</span>
            </Alert>
          )}

          {flowPhase === 'clash-review' && clashResult && (
            <>
              {clashResult.hasBlockingClash ? (
                <Alert variant="danger" className="mb-3">
                  An existing product matches this name. Use a match below, or
                  go back and change the source name.
                </Alert>
              ) : (
                <Alert variant="warning" className="mb-3">
                  Similar products were found. Use a match below, or continue to
                  research a new product.
                </Alert>
              )}
              <ListGroup>
                {clashResult.matches.map(match => (
                  <ListGroup.Item
                    key={match.productId}
                    className="d-flex justify-content-between align-items-start gap-3"
                  >
                    <div>
                      <div className="fw-semibold">
                        #{match.productId} — {match.name}
                      </div>
                      <div className="small text-body-secondary">
                        {match.productBrand}
                        {match.productCategoryName
                          ? ` · ${match.productCategoryName}`
                          : ''}
                      </div>
                      <div className="small">{match.clashReason}</div>
                    </div>
                    <Button
                      size="sm"
                      variant="primary"
                      disabled={isBusy}
                      onClick={() => void bindExisting(match.productId)}
                    >
                      Use this product
                    </Button>
                  </ListGroup.Item>
                ))}
              </ListGroup>
            </>
          )}

          {(flowPhase === 'researching' || flowPhase === 'form') && (
            <>
          {isResearching && (
            <Alert variant="info" className="d-flex align-items-center gap-2">
              <Spinner animation="border" size="sm" />
              <span>{progressMessage}</span>
            </Alert>
          )}
          {researchError && (
            <Alert variant="warning">
              {researchError} You can still fill in the form and create the
              product.
            </Alert>
          )}

          {existingMatches.length > 0 && (
            <div className="mb-4">
              <h6 className="mb-2">Already in catalog</h6>
              <ListGroup>
                {existingMatches.map(match => (
                  <ListGroup.Item
                    key={match.productId}
                    className="d-flex justify-content-between align-items-start gap-3"
                  >
                    <div>
                      <div className="fw-semibold">
                        #{match.productId} — {match.name}
                      </div>
                      <div className="small text-body-secondary">
                        {[match.brand, match.category]
                          .filter(Boolean)
                          .join(' · ')}
                        {match.price != null ? ` · ₱${match.price}` : ''}
                      </div>
                      {match.reason && (
                        <div className="small">{match.reason}</div>
                      )}
                    </div>
                    <Button
                      size="sm"
                      variant="primary"
                      disabled={isBusy}
                      onClick={() => void bindExisting(match.productId)}
                    >
                      Use this product
                    </Button>
                  </ListGroup.Item>
                ))}
              </ListGroup>
            </div>
          )}

          <Form>
            <Form.Group className="mb-3">
              <FloatingLabel label="Product Brand">
                <Form.Select
                  value={selectedBrand?.value ?? ''}
                  onChange={e => {
                    const id = Number(e.target.value);
                    setSelectedBrand(
                      e.target.value
                        ? brandOptions.find(o => o.value === id) ?? null
                        : null
                    );
                  }}
                  isInvalid={!!errors.productBrandId}
                  disabled={isResearching}
                >
                  <option value="">Select brand</option>
                  {brandOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Form.Select>
                <Form.Control.Feedback type="invalid">
                  {errors.productBrandId}
                </Form.Control.Feedback>
              </FloatingLabel>
              {proposal?.draft?.brandName && !selectedBrand && (
                <Form.Text className="text-warning">
                  AI suggested brand “{proposal.draft.brandName}” — pick the
                  matching catalog brand.
                </Form.Text>
              )}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Product Category</Form.Label>
              <CategorySelect
                leavesOnly
                isDisabled={isResearching}
                isInvalid={!!errors.productCategoryId}
                placeholder="Search category"
                value={selectedCategory?.value ?? null}
                valueLabel={selectedCategory?.label}
                onChange={(id, category) =>
                  setSelectedCategory(
                    id != null && category
                      ? {
                          value: id,
                          label: category.fullPath || category.name
                        }
                      : null
                  )
                }
              />
              {errors.productCategoryId && (
                <div className="invalid-feedback d-block">
                  {errors.productCategoryId}
                </div>
              )}
              {proposal?.draft?.categoryName && !selectedCategory && (
                <Form.Text className="text-warning">
                  AI suggested category “{proposal.draft.categoryName}” — pick
                  the matching catalog category.
                </Form.Text>
              )}
            </Form.Group>

            <Form.Group className="mb-3">
              <FloatingLabel label="Identifier / part code">
                <Form.Control
                  value={productIdentifier}
                  onChange={e => setProductIdentifier(e.target.value)}
                  disabled={isResearching}
                />
              </FloatingLabel>
            </Form.Group>

            <Form.Group className="mb-3">
              <FloatingLabel label="Product Name">
                <Form.Control
                  value={productName}
                  onChange={e => {
                    setNameManuallyEdited(true);
                    setProductName(e.target.value);
                  }}
                  isInvalid={!!errors.productName}
                  disabled={isResearching}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.productName}
                </Form.Control.Feedback>
              </FloatingLabel>
              <Form.Text className="text-muted">
                Auto-built as Brand | Identifier | Category
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <FloatingLabel label="Product Description">
                <Form.Control
                  as="textarea"
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  isInvalid={!!errors.description}
                  disabled={isResearching}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.description}
                </Form.Control.Feedback>
              </FloatingLabel>
            </Form.Group>

            <Form.Group className="mb-3">
              <FloatingLabel label="Selling Price">
                <Form.Control
                  value={sellingPrice}
                  onChange={e => setSellingPrice(e.target.value)}
                  isInvalid={!!errors.sellingPrice}
                  disabled={isResearching}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.sellingPrice}
                </Form.Control.Feedback>
              </FloatingLabel>
              <Form.Text className="text-muted">
                Online prices below are reference only.
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <FloatingLabel label="Barcode Label">
                <Form.Control
                  value={barcodeLabel}
                  onChange={e => {
                    setBarcodeManuallyEdited(true);
                    setBarcodeLabel(e.target.value);
                  }}
                  maxLength={40}
                  isInvalid={!!errors.barcodeLabel}
                  disabled={isResearching}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.barcodeLabel}
                </Form.Control.Feedback>
              </FloatingLabel>
            </Form.Group>

            <div className="mb-3">
              <div className="fw-semibold mb-2">Product images</div>
              <Form.Text className="text-muted d-block mb-2">
                Selected online photos are copied onto the new product. Upload
                your own if none were found.
              </Form.Text>
              {onlineImageUrls.length === 0 ? (
                <div className="small text-body-secondary mb-2">
                  No online photos found yet.
                </div>
              ) : (
                <div className="d-flex flex-wrap gap-3 mb-2">
                  {onlineImageUrls.map(url => {
                    const checked = selectedImageUrls.includes(url);
                    return (
                      <button
                        key={url}
                        type="button"
                        className="d-flex flex-column gap-1 text-start border-0 bg-transparent p-0"
                        style={{ width: 120 }}
                        disabled={isResearching}
                        onClick={() => toggleOnlineImage(url)}
                      >
                        <span className="small">
                          {checked ? 'Selected' : 'Use this'}
                        </span>
                        <img
                          src={url}
                          alt="Online product"
                          style={{
                            width: 120,
                            height: 120,
                            objectFit: 'cover',
                            border: checked
                              ? '2px solid var(--phoenix-primary)'
                              : '1px solid var(--phoenix-border-color)',
                            borderRadius: 4
                          }}
                          onError={event => {
                            event.currentTarget.style.opacity = '0.35';
                          }}
                        />
                        <a
                          href={url}
                          target="_blank"
                          rel="noreferrer"
                          className="small text-truncate"
                          onClick={event => event.stopPropagation()}
                        >
                          Open
                        </a>
                      </button>
                    );
                  })}
                </div>
              )}
              <Form.Control
                type="file"
                accept="image/*"
                multiple
                disabled={isResearching}
                isInvalid={!!errors.images && !hasProductImages()}
                onChange={handleFileChange}
              />
              {errors.images && (
                <div className="invalid-feedback d-block">{errors.images}</div>
              )}
            </div>

            <div className="mb-3">
              <div className="fw-semibold mb-2">OEM numbers</div>
              <div className="d-flex gap-2 mb-2">
                <Form.Control
                  value={oemDraft}
                  onChange={e => setOemDraft(e.target.value)}
                  placeholder="Add OEM code"
                  disabled={isResearching}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addOem();
                    }
                  }}
                />
                <Button
                  variant="phoenix-secondary"
                  onClick={addOem}
                  disabled={isResearching || !oemDraft.trim()}
                >
                  Add
                </Button>
              </div>
              {oemNumbers.length === 0 ? (
                <div className="small text-body-secondary">None yet</div>
              ) : (
                <div className="d-flex flex-wrap gap-2">
                  {oemNumbers.map(oem => (
                    <Button
                      key={oem}
                      size="sm"
                      variant="phoenix-secondary"
                      disabled={isResearching}
                      onClick={() =>
                        setOemNumbers(prev => prev.filter(item => item !== oem))
                      }
                    >
                      {oem} ×
                    </Button>
                  ))}
                </div>
              )}
            </div>

            <div className="mb-3">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <div className="fw-semibold">Fitment</div>
                <Button
                  size="sm"
                  variant="phoenix-secondary"
                  disabled={isResearching}
                  onClick={() => setFitments(prev => [...prev, emptyFitment()])}
                >
                  Add row
                </Button>
              </div>
              {fitments.length === 0 ? (
                <div className="small text-body-secondary">None yet</div>
              ) : (
                fitments.map((fitment, index) => (
                  <div
                    key={`fitment-${index}`}
                    className="row g-2 mb-2 align-items-center"
                  >
                    <div className="col">
                      <Form.Control
                        placeholder="Make"
                        value={fitment.make}
                        onChange={e =>
                          setFitments(prev =>
                            prev.map((row, i) =>
                              i === index ? { ...row, make: e.target.value } : row
                            )
                          )
                        }
                        disabled={isResearching}
                      />
                    </div>
                    <div className="col">
                      <Form.Control
                        placeholder="Model"
                        value={fitment.model}
                        onChange={e =>
                          setFitments(prev =>
                            prev.map((row, i) =>
                              i === index
                                ? { ...row, model: e.target.value }
                                : row
                            )
                          )
                        }
                        disabled={isResearching}
                      />
                    </div>
                    <div className="col">
                      <Form.Control
                        placeholder="Years"
                        value={fitment.yearRange ?? ''}
                        onChange={e =>
                          setFitments(prev =>
                            prev.map((row, i) =>
                              i === index
                                ? { ...row, yearRange: e.target.value }
                                : row
                            )
                          )
                        }
                        disabled={isResearching}
                      />
                    </div>
                    <div className="col">
                      <Form.Control
                        placeholder="Engine"
                        value={fitment.engine ?? ''}
                        onChange={e =>
                          setFitments(prev =>
                            prev.map((row, i) =>
                              i === index
                                ? { ...row, engine: e.target.value }
                                : row
                            )
                          )
                        }
                        disabled={isResearching}
                      />
                    </div>
                    <div className="col">
                      <Form.Control
                        placeholder="Usage"
                        value={fitment.usage ?? ''}
                        onChange={e =>
                          setFitments(prev =>
                            prev.map((row, i) =>
                              i === index
                                ? { ...row, usage: e.target.value }
                                : row
                            )
                          )
                        }
                        disabled={isResearching}
                      />
                    </div>
                    <div className="col-auto">
                      <Button
                        size="sm"
                        variant="phoenix-secondary"
                        disabled={isResearching}
                        onClick={() =>
                          setFitments(prev =>
                            prev.filter((_, i) => i !== index)
                          )
                        }
                      >
                        Remove
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </Form>

          {(onlinePrices.length > 0 || onlineReferences.length > 0) && (
            <div className="mt-3">
              <h6>Online prices / sources</h6>
              <p className="small text-body-secondary mb-2">
                Reference only — not saved as selling history.
              </p>
              <ListGroup>
                {onlinePrices.map((price, index) => (
                  <ListGroup.Item key={`price-${index}`}>
                    <div className="fw-semibold">
                      {price.source || 'Listing'}
                      {price.amount != null
                        ? ` · ${price.currency || 'PHP'} ${price.amount}`
                        : ''}
                    </div>
                    {price.url && (
                      <a href={price.url} target="_blank" rel="noreferrer">
                        {price.url}
                      </a>
                    )}
                  </ListGroup.Item>
                ))}
                {onlineReferences
                  .filter(
                    ref =>
                      ref.url &&
                      !onlinePrices.some(price => price.url === ref.url)
                  )
                  .map((ref, index) => (
                    <ListGroup.Item key={`ref-${index}`}>
                      <div className="fw-semibold">
                        {ref.source || ref.title || 'Source'}
                      </div>
                      <a href={ref.url} target="_blank" rel="noreferrer">
                        {ref.title || ref.url}
                      </a>
                    </ListGroup.Item>
                  ))}
              </ListGroup>
            </div>
          )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose} loading={isBusy}>
            Cancel
          </Button>
          {flowPhase === 'clash-review' && !clashResult?.hasBlockingClash && (
            <Button
              variant="primary"
              onClick={() => startResearchRef.current()}
              disabled={isBusy}
            >
              Continue to research
            </Button>
          )}
          {(flowPhase === 'researching' || flowPhase === 'form') && (
            <Button
              variant="primary"
              onClick={() => void handleSubmit()}
              loading={isBusy}
              disabled={isResearching || flowPhase !== 'form'}
            >
              {isCheckingClash ? 'Checking for clashes...' : 'Create product'}
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      <Modal
        show={showClashConfirm}
        onHide={() => setShowClashConfirm(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {clashResult?.hasBlockingClash
              ? 'Product name already exists'
              : 'Possible identifier conflict'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {clashResult?.hasBlockingClash ? (
            <Alert variant="danger" className="mb-3">
              An existing product has the exact same name. Use a match below, or
              go back and change the name.
            </Alert>
          ) : (
            <Alert variant="warning" className="mb-3">
              Similar products were found. Use a match below, or create a new
              product anyway.
            </Alert>
          )}
          <ListGroup>
            {clashResult?.matches.map(match => (
              <ListGroup.Item
                key={match.productId}
                action={!isSaving}
                disabled={isSaving}
                onClick={() => void bindExisting(match.productId)}
                className="text-start"
              >
                <div className="d-flex justify-content-between gap-2">
                  <div className="fw-semibold">
                    #{match.productId} — {match.name}
                  </div>
                  <span className="badge text-bg-primary">Use this</span>
                </div>
                <div className="small text-body-secondary">
                  {match.productBrand}
                  {match.productCategoryName
                    ? ` · ${match.productCategoryName}`
                    : ''}
                </div>
                <div className="small">{match.clashReason}</div>
              </ListGroup.Item>
            ))}
          </ListGroup>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowClashConfirm(false)}
            loading={isSaving}
          >
            Go back
          </Button>
          {!clashResult?.hasBlockingClash && (
            <Button
              variant="warning"
              onClick={() => void createProduct()}
              loading={isSaving}
            >
              Create anyway
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default CreateProductFromUnencodedModal;
